import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface DepthDistributionProps {
  pagesAtDepth: Record<number, number>;
}

export function DepthDistribution({ pagesAtDepth }: DepthDistributionProps) {
  // Transform data for the chart
  const data = Object.entries(pagesAtDepth)
    .map(([depth, count]) => ({
      depth: parseInt(depth, 10),
      label: `Depth ${depth}`,
      count,
    }))
    .sort((a, b) => a.depth - b.depth);

  // Color based on depth - deeper pages get warmer colors (indicating potential issues)
  const getBarColor = (depth: number): string => {
    if (depth === 0) return '#22c55e'; // green - homepage
    if (depth === 1) return '#84cc16'; // lime - good
    if (depth === 2) return '#eab308'; // yellow - acceptable
    if (depth === 3) return '#f97316'; // orange - getting deep
    return '#ef4444'; // red - too deep
  };

  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: '#888', fontSize: 11 }}
            axisLine={{ stroke: '#444' }}
            tickLine={{ stroke: '#444' }}
          />
          <YAxis
            tick={{ fill: '#888', fontSize: 11 }}
            axisLine={{ stroke: '#444' }}
            tickLine={{ stroke: '#444' }}
            allowDecimals={false}
            domain={[0, Math.ceil(maxCount * 1.1)]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: '#fff' }}
            itemStyle={{ color: '#888' }}
            formatter={(value: number) => [`${value} page${value !== 1 ? 's' : ''}`, 'Pages']}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.depth)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex justify-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#22c55e' }} />
          Homepage
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#84cc16' }} />
          1 Click
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#eab308' }} />
          2 Clicks
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#f97316' }} />
          3 Clicks
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }} />
          4+ Clicks
        </span>
      </div>
    </div>
  );
}
